// ─────────────────────────────────────────────────────────────────────────────
// stroke_resample.ts — Map a hand-drawn stroke onto a clean parametric path
//
// When the autoshape recogniser replaces a wobbly user stroke with a
// clean line / circle / polygon, we want the new figure to still
// FEEL like the user's hand — pressure variations, tilt-driven width
// asymmetry, the timing pattern. The clean geometry contributes the
// position; the original stroke contributes the kinematics.
//
// `resampleAlong` walks the original stroke and, for each sample,
// places it at the corresponding arc-length fraction along the new
// parametric geometry. The output StrokePoint[] carries the new (x,y)
// but inherits pressure / tilt / timestamp from the source — perfect
// food for the ink layer's brush dispatch.
//
// `pathSamplerForEdges` is the line-strip helper for figures with
// explicit edges (line / polygon / rectangle / open arrow). Circle
// / ellipse have a dedicated `pathSamplerForEllipse`.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint } from '../shared/notebook_model';

/** Returns the (x, y) coordinate at fractional arc-length t (0..1)
 *  along some clean parametric path. Caller composes one of these
 *  per shape kind and feeds it into resampleAlong. */
export type GeometrySampler = (t: number) => { x: number; y: number };

/** Walk the source stroke; for each sample place it at the same
 *  arc-length fraction along the target geometry. Pressure, tilt,
 *  twist, timestamp are preserved verbatim — only x/y move. */
export function resampleAlong(
  source: readonly StrokePoint[],
  geometry: GeometrySampler,
): StrokePoint[] {
  if (source.length < 2) return [];
  // Cumulative arc length of the source.
  const lens: number[] = [0];
  let total = 0;
  for (let i = 1; i < source.length; i++) {
    const dx = source[i].x - source[i - 1].x;
    const dy = source[i].y - source[i - 1].y;
    total += Math.sqrt(dx * dx + dy * dy);
    lens.push(total);
  }
  if (total <= 0) return [];

  const out: StrokePoint[] = [];
  for (let i = 0; i < source.length; i++) {
    const t = lens[i] / total;     // 0..1
    const pos = geometry(t);
    const sp = source[i];
    out.push({
      x: pos.x,
      y: pos.y,
      pressure: sp.pressure,
      tiltX: sp.tiltX,
      tiltY: sp.tiltY,
      twist: sp.twist,
      timestamp: sp.timestamp,
    });
  }
  return out;
}

/** Sampler for an ordered chain of vertices forming line segments
 *  (line, polygon, rectangle, open arrow). Treats the edges as a
 *  continuous polyline: t ∈ [0, 1] maps to arc-length fraction
 *  across the entire chain. */
export function pathSamplerForEdges(
  vertices: ReadonlyArray<{ x: number; y: number }>,
  edges: ReadonlyArray<{ from: number; to: number }>,
): GeometrySampler | null {
  if (vertices.length < 2 || edges.length === 0) return null;

  // Build a flat polyline from the edge chain. Most recogniser
  // outputs already give edges in chain order; for safety we still
  // walk by edge.from / edge.to indices rather than assuming
  // sequential vertex order.
  const chain: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    const a = vertices[e.from];
    const b = vertices[e.to];
    if (!a || !b) continue;
    if (i === 0) chain.push({ x: a.x, y: a.y });
    chain.push({ x: b.x, y: b.y });
  }
  if (chain.length < 2) return null;

  const segLens: number[] = [];
  let total = 0;
  for (let i = 1; i < chain.length; i++) {
    const dx = chain[i].x - chain[i - 1].x;
    const dy = chain[i].y - chain[i - 1].y;
    const d  = Math.sqrt(dx * dx + dy * dy);
    segLens.push(d);
    total += d;
  }
  if (total <= 0) return null;

  return (t: number) => {
    const target = Math.max(0, Math.min(1, t)) * total;
    let acc = 0;
    for (let i = 0; i < segLens.length; i++) {
      const next = acc + segLens[i];
      if (target <= next || i === segLens.length - 1) {
        const segT = segLens[i] > 0 ? (target - acc) / segLens[i] : 0;
        const a = chain[i];
        const b = chain[i + 1];
        return { x: a.x + (b.x - a.x) * segT, y: a.y + (b.y - a.y) * segT };
      }
      acc = next;
    }
    // unreachable — loop returns above
    return { x: chain[chain.length - 1].x, y: chain[chain.length - 1].y };
  };
}

/** Sampler for circle / ellipse. t maps linearly to angle 0..2π
 *  starting at the right-most point (angle 0) for visual consistency
 *  with parametric circle conventions; if the recogniser provides a
 *  preferred start angle later this can grow a phase parameter. */
export function pathSamplerForEllipse(
  cx: number, cy: number, rx: number, ry: number,
): GeometrySampler {
  const RR = ry > 0 ? ry : rx;
  return (t: number) => {
    const a = (Math.max(0, Math.min(1, t))) * Math.PI * 2;
    return { x: cx + rx * Math.cos(a), y: cy + RR * Math.sin(a) };
  };
}
